import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { forkJoin, take, takeUntil } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { EChartsOption } from 'echarts';
import { startOfDay } from 'date-fns';

import { FilterControlComponent } from '@shared/components/vehicle-list/filter-control/filter-control.component';
import { FilterVehicleType } from '@src/app/enum/filters-vehicle.enum';
import { IFilterVehicle, IFilterVehicleGroup, IFilterVehicleOption, ISubmitFilters } from '@src/app/interfaces/filters-vehicle.interface';
import { VehicleService } from '@commons/services/vehicle/vehicle.service';
import { Destroyable } from '@src/app/utils/Destroyable';
import { GetVehicleParamNameByIds, Vehicle } from '@commons/models/domain/Vehicle';
import { DatePickerComponent } from '@src/app/standalone-components/date-picker/date-picker.component';
import { GetParameterValueListPostdata, IAxisConfig, IChartBuildConfig, IChartBuildConfigTooltipData, IConfigChartParams, IGetChartData } from '@src/app/interfaces/chart-params.interface';
import { WebAccessService } from '@commons/services/web.access/web-access.service';
import { TooltipBuilderCarsParams } from '@src/app/standalone-components/charts/chart-tooltip-builder';
import { YAxisConfigBuilderCarsParams } from '@src/app/standalone-components/charts/chart-config-y-axis-builder';
import { ChartConfigBuilder } from '@src/app/standalone-components/charts/chart-config-builder';
import { ChartLineComponent } from '@src/app/standalone-components/charts/chart-line/chart-line.component';
import { CHART_CARS_PARAMS_CONFIG } from '@src/app/utils/chart/chart-config';
import { GenerateXlsFileChartData } from '@src/app/standalone-components/charts/chart-get-xls';
import { ParameterTypeEnum } from '@commons/enums/parameter-type.enum';
import { PdfGenerator } from '@src/app/utils/pdf-generator/pdf-generator';
import { IPdfDataObject } from '@src/app/interfaces/pdf.interface';
import { formatDate } from '@src/app/utils/date';
import { PermissionService } from '@shared/services/permission.service';
import { DatepickerTimeRangeTips } from '@src/app/enum/datepicker.enum';
import { PipesModule } from '@commons/pipes/pipes.module';
import { TranslateByTextPipe } from '@commons/pipes/translate-by-text.pipe';
import { IOrganizationVehicleUnit } from '@src/app/interfaces/api/organization-units.interface';
import { UserOrganizationsUnitsService } from '@src/app/services/api/user-organizations-units.service';

@Component({
  selector: 'app-parameter-cars-chart',
  standalone: true,
  imports: [CommonModule, TranslateModule, FilterControlComponent, DatePickerComponent, FormsModule, ButtonModule, ChartLineComponent, PipesModule],
  templateUrl: './parameter-cars-chart.component.html',
  styleUrls: ['./parameter-cars-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TranslateByTextPipe],
})
export class ParameterCarsChartComponent extends Destroyable implements OnInit {
  @ViewChild('filterParams') public filterParamsRef: FilterControlComponent;
  @ViewChild('chartLineRef') public chartLineRef: ChartLineComponent;

  public filtersVehicleConfig: IFilterVehicle;
  public filtersVehicleCurrentFilters: ISubmitFilters;

  public filtersParamsConfig: IFilterVehicle;
  public filtersParamsCurrentFilters: ISubmitFilters<number[]>;

  public filtersSelectedVehicle: Vehicle;

  public readonly datepickerTimeRangeTipsData = [DatepickerTimeRangeTips.Today, DatepickerTimeRangeTips.Yesterday, DatepickerTimeRangeTips.DayBeforeYesterday];
  public selectedDate: Date;
  public isLoadingChart: boolean;
  private paramsNameList: Map<number, string>;

  public chartData: IGetChartData[] = [];
  public ChartConfig: EChartsOption;

  private VehicleSpeedID: number;

  constructor(
    private translate: TranslateService,
    private changeDetRef: ChangeDetectorRef,
    private vehicleService: VehicleService,
    private webAccessService: WebAccessService,
    private permissionService: PermissionService,
    private translateByText: TranslateByTextPipe,
    private userOrganizationsUnitsService: UserOrganizationsUnitsService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.setFiltersConfig();
  }

  private get vehicleData(): Vehicle {
    return this.vehicleService.getVehicleDetails(this.filtersVehicleCurrentFilters.selectedControlValue);
  }

  private get selectedDateFormated(): string {
    return formatDate('dd MMMM yyyy', this.selectedDate);
  }

  public generatePdfFile(): void {
    const vehicle = this.vehicleData;
    const chartDataDate = this.selectedDateFormated;

    const userData: IPdfDataObject[] = [
      { index: 0, text: `${ this.translate.instant('chartParams.dataFromDate') }: ${ chartDataDate }` },
      { index: 1, text: `${ this.translate.instant('labels.vehicle') }: ${ vehicle.Nm }` },
      { index: 2, image: this.chartLineRef.getChartImageBase64() },
    ];

    const generator = new PdfGenerator(userData, {
      spacing: 10,
      topMargin: 20,
      sideMargin: 20,
      fileName: `${this.translate.instant('chartParams.title')} ${ vehicle.Nm } ${ chartDataDate }.pdf`,
    });
    generator.generatePdf();
  }

  public generateXlsFile(): void {
    const translateParamsName = new Map<number, string>();

    this.paramsNameList.forEach((_, key) =>
      translateParamsName.set(key, this.translateByText.transform(this.paramsNameList.get(key) as string))
    );

    const GenerateXlsFile = new GenerateXlsFileChartData(
      this.chartData,
      this.filtersParamsCurrentFilters.selectedControlValue,
      translateParamsName,
    );

    const vehicle = this.vehicleData;
    GenerateXlsFile.getXLSFile(`${this.translate.instant('chartParams.title')} ${ vehicle.Nm } ${ this.selectedDateFormated }`);
  }

  public getChartDataImage(): void {
    const vehicle = this.vehicleData;
    this.chartLineRef.getImageDataChart(`${this.translate.instant('chartParams.title')} ${ vehicle.Nm } ${ this.selectedDateFormated }`);
  }

  public submitFilters(): void {
    this.getChartData();
  }

  public onChangeVehicleFilters(data: ISubmitFilters, filterData: IFilterVehicle): void {
    console.log(data);

    this.resetVehicleParamsFilter();
    if (data.selectedControlValue) {
      this.getVehicleDataById(data.selectedControlValue);
    }
    this.filtersVehicleCurrentFilters = data.selectedControlValue ? {...data} : (null as any);
  }

  public onChangeParamsFilters(data: ISubmitFilters, filterData: IFilterVehicle): void {
    this.filtersParamsCurrentFilters = {...data};
  }

  private resetVehicleParamsFilter(): void {
    this.filterParamsRef.clearFilters(null as any);
  }

  private setFiltersConfig(): void {
    this.filtersVehicleConfig = {
      filterLabel: this.translate.instant('labels.vehicle'),
      type: FilterVehicleType.Group,
      dropdownLabel: this.translate.instant('chartParams.availableCars'),
      options: [],
      isVisibleSelectedOptionLabel: true,
      panelWidthPx: 400,
    };
    this.filtersParamsConfig = {
      filterLabel: this.translate.instant('vehicle-details-tabs.parameters'),
      type: FilterVehicleType.MultipleCheckbox,
      dropdownLabel: this.translate.instant('chartParams.availableParams'),
      options: [],
    };
    this.changeDetRef.markForCheck();
    this.getVehiclesList();
  }

  private getVehiclesList(): void {
    const requests = {
      vehicles: this.vehicleService.getList$().pipe(take(1)),
      units: this.userOrganizationsUnitsService.GetOrganizationalUnits(),
    };

    forkJoin(requests)
    .pipe(
      takeUntil(this.destroy$),
    )
    .subscribe(
      ({ vehicles, units }) => {
        this.setVehicleList(vehicles, units);
      }
    );
  }

  private setVehicleList(data: Vehicle[], units: IOrganizationVehicleUnit[]): void {
    const { list } = this.vehicleService.GetVehiclesGroupedV2(data, units);

    this.filtersVehicleConfig = {
      ...this.filtersVehicleConfig,
      options: list as IFilterVehicleGroup[],
    }
  }

  private getVehicleDataById(idVehicle: number): void {
    this.vehicleService.getDetails$(idVehicle).pipe(take(1)).subscribe({
      next: (data) => {
        this.setVehicleParams(data);
      }
    });
  }

  private setVehicleParams(vehicle: Vehicle): void {
    this.filtersSelectedVehicle = vehicle;
    const params = [...vehicle.ControlIndicators || []].filter(item => CHART_CARS_PARAMS_CONFIG.get(item.Tp) && !item.Nm.includes('#'));

    this.filtersParamsConfig = {
      ...this.filtersParamsConfig,
      options: params.map(item => ({ label: this.translateByText.transform(item.Nm), value: item.Id, data: item })) as IFilterVehicleOption[],
    };
    this.VehicleSpeedID = params.find(item => item.Tp === ParameterTypeEnum.Speed)?.Id || null as any;
  }

  private getParameterValueBodyRequest(): GetParameterValueListPostdata {
    const ParameterIds = [this.VehicleSpeedID, ...this.filtersParamsCurrentFilters.selectedControlValue];
    return {
      ParameterIds,
      RequestedDay: startOfDay(this.selectedDate).toISOString(),
    };
  }

  private setParamsName(): void {
    const paramNameList = new Map();
    for (const item of this.filtersParamsCurrentFilters.selectedControlValue) {
      const paramName = this.getVehicleParamNameBydId(item, this.filtersSelectedVehicle);
      paramNameList.set(item, paramName);
    }
    this.paramsNameList = paramNameList;
  }

  private getChartData(): void {
    this.isLoadingChart = true;
    this.ChartConfig = null as any;
    this.changeDetRef.markForCheck();

    this.webAccessService.GetParameterValueList(this.getParameterValueBodyRequest()).pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        this.chartData = data;

        if (data.length) {
          this.setParamsName();
          this.displayChart(data);
        }

        this.isLoadingChart = false;
        this.changeDetRef.markForCheck();
      },
    });
  }

  private getVehicleParamNameBydId(id: number, vehicle: Vehicle): string {
    return new GetVehicleParamNameByIds(id, vehicle).paramName;
  }

  private displayChart(chartData: IGetChartData[]): void {
    const TooltipBuilder = new TooltipBuilderCarsParams(this.translate);
    const YAxisConfigBuilder = new YAxisConfigBuilderCarsParams();

    const selectedParams: number[] = this.filtersParamsCurrentFilters.selectedControlValue;

    const tooltipData: IChartBuildConfigTooltipData[] = selectedParams.map(
      (parameterID: number) => {
        const parameter = this.getParamConfigByControlID(this.filtersSelectedVehicle, parameterID);

        return {
          seriesName: this.translateByText.transform(this.paramsNameList.get(parameterID) as string),
          color: parameter.config.color,
          unit: parameter.config.unit,
          paramType: parameter.type,
          parameterID,
        }
      }
    );

    // oy
    const axisConfigs: IAxisConfig[] = selectedParams
    .map(
      (parameterID: number) => {
        const parameter = this.getParamConfigByControlID(this.filtersSelectedVehicle, parameterID);
        return {
          parameterID,
          parameter,
        };
      }
    )
    .filter(item => item.parameter.yAxisConfig)
    .map(
      (data, index) => {
        return {
          y: {
            type: data.parameter.yAxisConfig?.type || 'value',
            name: this.translateByText.transform(this.paramsNameList.get(data.parameterID) as string),
            typeParameter: data.parameter.type,
            unit: data.parameter.config.unit,
            color: data.parameter.config.color,
            ...data.parameter.yAxisConfig,
          }
        };
      }
    );

    let theBiggestNumberInChart = 0;

    const series = selectedParams
    .map(
      (parameterID, index) => {
        const parameter = this.getParamConfigByControlID(this.filtersSelectedVehicle, parameterID);
        return {
          name: this.translateByText.transform(this.paramsNameList.get(parameterID) as string),
          type: parameter.chartType as any,
          parameterType: parameter.type,
          data: chartData.map(item => {
            const seriesValue = item[parameterID] || 0;
            if (seriesValue > theBiggestNumberInChart) {
              theBiggestNumberInChart = seriesValue;
            }
            return seriesValue;
          }),
          yAxisIndex: parameter.yAxisConfig ? index : undefined,
          ...(parameter.config || {}),
        };
      }
    );

    const ChartConfigOptions: IChartBuildConfig = {
      customParams: {
        isDataZoom: true,
        tooltipData,
        axisConfigs,
        legend: [...this.paramsNameList.values()].map(el => this.translateByText.transform(el)),
        series,
        theBiggestNumberInChart,
      }
    };

    this.ChartConfig = new ChartConfigBuilder(TooltipBuilder, YAxisConfigBuilder).BuildConfig(ChartConfigOptions, chartData);
    this.changeDetRef.detectChanges();
  }

  private getParamConfigByControlID(vehicle: Vehicle, parameterID: number): IConfigChartParams {
    const param = vehicle.ControlIndicators?.find(item => item.Id === parameterID);
    const paramUnit = CHART_CARS_PARAMS_CONFIG.get(param!.Tp);
    return paramUnit!;
  }

}
